/**
 * Writes that survive a dead lift.
 *
 * The people using this app are in basements, service corridors and car parks.
 * A tap that fails because the phone lost signal for ten seconds does not read
 * as "no signal" — it reads as "this app does not work", and the next time she
 * simply does not tap it.
 *
 * So a write that fails on the network rather than on the server is kept and
 * replayed. Only writes that are safe to repeat: marking a task started, adding
 * an update, clocking in and out. Money and approvals are deliberately not
 * queued — a payment or an approval that lands twenty minutes later, without
 * the person knowing, is worse than one that failed loudly.
 *
 * IndexedDB rather than localStorage because a photo caption plus a queue of
 * writes is easily past the 5MB string limit, and because localStorage is
 * synchronous on the main thread.
 */

const DB_NAME = 'famanage-outbox';
const STORE = 'writes';
const VERSION = 1;

export interface QueuedWrite {
  id: string;
  path: string;
  method: string;
  body: string | null;
  /** What the person was doing, for the banner. */
  label: string;
  queuedAt: string;
  attempts: number;
}

/**
 * Paths whose writes may be replayed later.
 *
 * An allowlist, not a denylist. A new endpoint is not silently queueable
 * because somebody forgot to exclude it — the failure mode of a denylist here
 * is a duplicated payment, and nobody wants to find that out in production.
 */
const REPLAYABLE = [
  /^\/v1\/operations\/[^/]+\/transitions\/(in_progress|seen|waiting_proof|waiting_approval)$/,
  /^\/v1\/operations\/[^/]+\/updates$/,
  /^\/v1\/operations\/[^/]+\/mark-seen$/,
  /^\/v1\/operations\/[^/]+\/checklist\/[^/]+$/,
  /^\/v1\/shifts\/clock-(in|out)$/,
];

export function isReplayable(path: string, method: string): boolean {
  if (!['POST', 'PATCH', 'PUT'].includes(method.toUpperCase())) return false;
  return REPLAYABLE.some((re) => re.test(path));
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function enqueue(write: Omit<QueuedWrite, 'id' | 'queuedAt' | 'attempts'>) {
  const row: QueuedWrite = {
    ...write,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    queuedAt: new Date().toISOString(),
    attempts: 0,
  };
  await withStore('readwrite', (s) => s.add(row));
  notify();
  return row;
}

export async function pending(): Promise<QueuedWrite[]> {
  try {
    const all = await withStore<QueuedWrite[]>(
      'readonly',
      (s) => s.getAll() as IDBRequest<QueuedWrite[]>,
    );
    return all.sort((a, b) => (a.queuedAt < b.queuedAt ? -1 : 1));
  } catch {
    // A browser with IndexedDB blocked (private mode, some embedded webviews)
    // still has to work — it simply has no outbox.
    return [];
  }
}

async function drop(id: string) {
  await withStore('readwrite', (s) => s.delete(id));
}

async function bump(row: QueuedWrite) {
  await withStore('readwrite', (s) => s.put({ ...row, attempts: row.attempts + 1 }));
}

// Anything that wants to re-render when the queue changes.
const listeners = new Set<() => void>();
export function onQueueChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  for (const fn of listeners) fn();
}

/** Give up on a write after this many failed replays. */
const MAX_ATTEMPTS = 8;

/**
 * Replay everything, oldest first.
 *
 * Order matters: "started" before "finished" on the same task is the difference
 * between a valid transition and a 422. One at a time, and a failure stops the
 * run rather than racing ahead — the next reconnect will pick it up.
 *
 * A write the server rejects on its merits (4xx that is not 408/429) is dropped,
 * not retried forever: the state has moved on, and replaying it every time the
 * phone finds signal is a queue that never empties.
 */
export async function flush(
  send: (w: QueuedWrite) => Promise<{ ok: boolean; status: number }>,
): Promise<{ sent: number; failed: number }> {
  let sent = 0;
  let failed = 0;
  for (const w of await pending()) {
    if (w.attempts >= MAX_ATTEMPTS) {
      await drop(w.id);
      failed++;
      continue;
    }
    try {
      const res = await send(w);
      if (res.ok) {
        await drop(w.id);
        sent++;
      } else if (res.status >= 400 && res.status < 500 && ![408, 429].includes(res.status)) {
        await drop(w.id);
        failed++;
      } else {
        await bump(w);
        break;
      }
    } catch {
      // Still offline. Leave the queue exactly as it is.
      await bump(w);
      break;
    }
  }
  if (sent || failed) notify();
  return { sent, failed };
}

/** Wire the automatic replay: on reconnect, and once on load. */
export function startOutbox(send: (w: QueuedWrite) => Promise<{ ok: boolean; status: number }>) {
  const run = () => {
    void flush(send);
  };
  window.addEventListener('online', run);
  // Coming back to the app is as good a signal as `online` — a phone that
  // never fired the event still knows it has signal when you look at it.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) run();
  });
  if (navigator.onLine) run();
  return () => window.removeEventListener('online', run);
}
